/** A label's dimensions, in inches. */
export interface LabelSize {
  w: number;
  h: number;
}

/** The box fields {@link rescaleElements} reads — loose enough for both a
 *  resolved element and a variation's stored (nullable) one. */
export interface ScalableBox {
  xIn?: number | null;
  yIn?: number | null;
  wIn?: number | null;
  hIn?: number | null;
  rotation?: number | null;
}

/** Rounds to 0.01", the precision the inspector shows and edits. */
const toHundredths = (n: number) => Math.round(n * 100) / 100;

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(Math.max(n, lo), Math.max(lo, hi));

/**
 * Carries an arrangement over to a new label size, keeping every element where
 * it was *proportionally*: x and width scale with the label's width, y and
 * height with its height. Positions are absolute inches, so without this a
 * change of stock (or a flip to landscape) leaves the design stranded in one
 * corner or hanging off the edge.
 *
 * A box turned 90°/270° occupies its height across and its width down, so its
 * size scales along the axes it actually covers; its centre scales with the
 * label either way. Results are rounded to 0.01" and kept inside the label.
 *
 * Font size (`scale`) is left alone on purpose: text already auto-shrinks to
 * fit its box, and scaling it by one ratio would compound on every
 * portrait/landscape flip.
 * @param elements - The arrangement, in inches on the `from` label
 * @param from - The label size the arrangement was laid out on
 * @param to - The new label size
 * @returns The rescaled elements (unchanged if either size is degenerate)
 */
export function rescaleElements<T extends ScalableBox | null>(
  elements: readonly T[],
  from: LabelSize,
  to: LabelSize,
): T[] {
  if (!(from.w > 0 && from.h > 0 && to.w > 0 && to.h > 0)) return [...elements];
  const rx = to.w / from.w;
  const ry = to.h / from.h;
  if (rx === 1 && ry === 1) return [...elements];

  return elements.map((e) => {
    if (
      e == null ||
      e.xIn == null ||
      e.yIn == null ||
      e.wIn == null ||
      e.hIn == null
    ) {
      return e;
    }
    const quarterTurns = ((Math.round((e.rotation ?? 0) / 90) % 4) + 4) % 4;
    const sideways = quarterTurns % 2 === 1;

    // The on-label (visual) extent after rotation, scaled and kept on the label.
    const visW = Math.min((sideways ? e.hIn : e.wIn) * rx, to.w);
    const visH = Math.min((sideways ? e.wIn : e.hIn) * ry, to.h);
    const cx =
      clamp((e.xIn + e.wIn / 2) * rx - visW / 2, 0, to.w - visW) + visW / 2;
    const cy =
      clamp((e.yIn + e.hIn / 2) * ry - visH / 2, 0, to.h - visH) + visH / 2;

    // Back to the stored, unrotated box about the same centre.
    const wIn = Math.max(0.01, toHundredths(sideways ? visH : visW));
    const hIn = Math.max(0.01, toHundredths(sideways ? visW : visH));
    return {
      ...e,
      xIn: toHundredths(cx - wIn / 2),
      yIn: toHundredths(cy - hIn / 2),
      wIn,
      hIn,
    };
  });
}
